<template>
  <LoadingSpinner v-if="isLoading || companiesLoading || subLoading" />

  <div v-else >
    <PageHeader title="Edit Subcontractor" />

    <div class="shadow p-5 mt-2 mb-5 bg-white rounded">
      <SubcontractorForm
        :sub="sub"
        :trades="trades"
        :companies="companies"
        :is-update="true"
        :save-function="updateSub"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import { Subcontractor } from '@/models/subcontractor'
import PageHeader from '@/components/app/AppPageHeader.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import SubcontractorService from '@/services/SubcontractorService'
import LaborService from '@/services/LaborService'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRouter, useRoute } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import SubcontractorForm from './components/SubcontractorForm.vue'

useHead({ title: 'Edit Subcontractor' })

const $router = useRouter()
const $route = useRoute()
const { notify } = useNotification()
const queryClient = useQueryClient()

const subId = $route.params.id as string || ''
const subService = new SubcontractorService()
const laborService = new LaborService()

const { data: sub, isLoading:subLoading } = useQuery({
  queryKey: ['sub', subId],
  queryFn: () => subService.find(subId)
})

const { data: trades, isLoading } = useQuery({
  queryKey: ['trades'],
  queryFn: () => laborService.all().then((labors) => labors.map((labor) => ({ value: labor.id, label: labor.name })))
})

const { data:companies, isLoading: companiesLoading } = useQuery({
  queryKey: ['companies'],
  queryFn: () => subService.getAllCompanies().then((companies) => companies.map((company) => ({ value: company.id, label: company.name })))
})

const { mutateAsync: updateSub } = useMutation({
  mutationFn: (sub: Subcontractor) => subService.update(subId, sub),
  onSuccess(sub: Subcontractor) {
    queryClient.setQueryData(['subcontractor', sub.id], sub)
    queryClient.invalidateQueries({ queryKey: ['subcontractors'] })
    if (sub.isCompany) {
      notify({
        type: 'success',
        title: 'Company updated',
        text: 'Company updated successfully'
      })
    } else {
      notify({
        type: 'success',
        title: 'Subcontractor updated',
        text: 'Subcontractor updated successfully'
      })
    }
    $router.push({ name: 'ShowSubcontractor', params: { id: sub.id } })
  }
})
</script>

